import { ApiService } from '../apiService'
import { resolveApiUrl } from '../client'
import { tokenStorage } from '@services/tokenStorage'
import {
  AdminAdventuresListParams,
  AdminAdventuresListResponse,
  AdminAdventureStats,
  AdminAdventureDeleteResult,
  AdventureImportResult,
  AdventureImportParams,
  AdventureBundleImportResult,
  AdventureBundleImportParams,
  AdminUserDto,
  AdminUsersListParams,
  AdminUsersListResponse,
  UpdateUserFlagsRequest,
  UpdateUserFlagsResponse,
  UpdateUserStatusRequest,
  UpdateUserRolesRequest,
  UserConnectionDto,
  AdminSessionsListParams,
  AdminSessionsListResponse,
  AdminSessionDetailDto,
  AdminSessionStatsDto,
  SessionLocationLogDto,
} from '../types/admin'

export const adminApi = {
  // Get all adventures with filtering and pagination
  getAdventures: (params?: AdminAdventuresListParams) =>
    ApiService.get<AdminAdventuresListResponse>('/admin/adventures', params),

  // Get adventure statistics
  getAdventureStats: () =>
    ApiService.get<AdminAdventureStats>('/admin/adventures/stats'),

  // Export adventure (returns JSON data)
  exportAdventure: (adventureId: string) =>
    ApiService.get<unknown>(`/api/adventures/${adventureId}/export`),

  // Download adventure export as file
  downloadAdventureExport: async (adventureId: string, filename?: string) => {
    // Use fetch directly for blob download
    const token = tokenStorage.getAccessToken()
    const response = await fetch(resolveApiUrl(`/admin/adventures/${adventureId}/export/bundle`), {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })

    if (!response.ok) {
      throw new Error('Failed to download adventure export')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || `adventure-${adventureId}-export.zip`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  // Import adventure from JSON
  importAdventure: (data: unknown, params?: AdventureImportParams) =>
    ApiService.post<AdventureImportResult>('/api/adventures/import', data, {
      params: {
        importAsDraft: params?.importAsDraft ?? true,
        skipTeamMembers: params?.skipTeamMembers ?? true,
      },
    }),

  // Import adventure bundle (ZIP file)
  importAdventureBundle: (file: File, params?: AdventureBundleImportParams) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<AdventureBundleImportResult>(
      `/api/adventures/import/bundle?importAsDraft=${params?.importAsDraft ?? true}&skipTeamMembers=${params?.skipTeamMembers ?? true}&importMediaLibrary=${params?.importMediaLibrary ?? true}&importShareCodes=${params?.importShareCodes ?? true}&regenerateQRCodes=${params?.regenerateQRCodes ?? true}`,
      formData,
    )
  },

  // Delete adventure and all related data
  deleteAdventure: (adventureId: string) =>
    ApiService.delete<AdminAdventureDeleteResult>(`/admin/adventures/${adventureId}`),

  // ========================
  // User Management
  // ========================

  // Get all users with filtering and pagination
  getUsers: (params?: AdminUsersListParams) =>
    ApiService.get<AdminUsersListResponse>('/admin/users', params),

  // Get single user by ID
  getUser: (userId: string) =>
    ApiService.get<AdminUserDto>(`/admin/users/${userId}`),

  // Update user status (active/inactive)
  updateUserStatus: (userId: string, request: UpdateUserStatusRequest) =>
    ApiService.put<void>(`/admin/users/${userId}/status`, request),

  // Update user roles
  updateUserRoles: (userId: string, request: UpdateUserRolesRequest) =>
    ApiService.put<void>(`/admin/users/${userId}/roles`, request),

  // Update user flags (add/remove bitwise flags)
  updateUserFlags: (userId: string, request: UpdateUserFlagsRequest) =>
    ApiService.put<UpdateUserFlagsResponse>(`/admin/users/${userId}/flags`, request),

  // Get user connections (OAuth providers)
  getUserConnections: (userId: string) =>
    ApiService.get<UserConnectionDto[]>(`/admin/users/${userId}/connections`),

  // Delete user
  deleteUser: (userId: string) =>
    ApiService.delete<void>(`/admin/users/${userId}`),

  // ========================
  // Session Management
  // ========================

  // Get sessions with filtering and pagination
  getSessions: (params?: AdminSessionsListParams) =>
    ApiService.get<AdminSessionsListResponse>('/admin/sessions', params),

  // Get session statistics
  getSessionStats: (adventureId?: string) =>
    ApiService.get<AdminSessionStatsDto>('/admin/sessions/stats', adventureId ? { adventureId } : undefined),

  // Get single session by ID
  getSession: (sessionId: string) =>
    ApiService.get<AdminSessionDetailDto>(`/admin/sessions/${sessionId}`),

  // Get session location logs
  getSessionLocations: (sessionId: string) =>
    ApiService.get<SessionLocationLogDto[]>(`/admin/sessions/${sessionId}/locations`),
}
